"use client";

import {
  getTestimonials,
  TestimonialCard,
  type Testimonial,
} from "@entities/testimonials";
import type { WithTotalCount } from "@shared/api";
import type { Locale } from "@shared/i18n";
import { Container } from "@shared/ui/container";
import { Spinner } from "@shared/ui/spinner";
import { useLocale } from "next-intl";
import { useCallback, useState, useTransition } from "react";
import { useInView } from "react-intersection-observer";

export type GridProps = {
  initialState: WithTotalCount<Testimonial[]>;
};

export function Grid({ initialState }: GridProps) {
  const locale = useLocale() as Locale;

  const [{ data: testimonials, total }, setTestimonials] =
    useState(initialState);
  const [page, setPage] = useState(1);
  const [isPending, startTransition] = useTransition();

  const { ref: bottomRef } = useInView({
    threshold: 0.1,
    rootMargin: "100px 0px",
    onChange: (inView) => {
      if (inView && total > testimonials.length + 1 && !isPending) {
        fetchNextPage();
      }
    },
  });

  const fetchNextPage = useCallback(async () => {
    startTransition(async () => {
      const result = await getTestimonials(locale, page);
      setTestimonials((prev) => ({
        data: [...prev.data, ...result.data],
        total: result.total,
      }));
      setPage((prev) => prev + 1);
    });
  }, [locale, page]);

  return (
    <Container className={"bg-gray-200"}>
      {testimonials.length ? (
        <>
          <div
            className={
              "grid lg:grid-cols-2 2xl:grid-cols-3 gap-8 animate-fade-in"
            }
          >
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={`testimonials:${index}`}
                showRating={true}
                backgroundMode="light"
                testimonial={testimonial}
                className={"text-black-pearl-950"}
              />
            ))}
          </div>

          {isPending && (
            <Spinner size={"sm"} className={"!w-fit !max-w-fit !h-full"} />
          )}

          {total > testimonials.length + 1 && (
            <div ref={bottomRef} className="h-4 w-full" />
          )}
        </>
      ) : (
        <p className={"m-auto text-center text-black-pearl-950 pb-24"}>
          Nenhum resultado encontrado.
        </p>
      )}
    </Container>
  );
}
